import { useEffect, useCallback } from "react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Shared";
import { HairBand, HairEyebrow, HairHeading, NumberedColumns, DoctorBlock, PathwayStrip, PracticeStandards } from "@/components/HairShared";
import { CompactPractitioner, OfferLine, ProofStrip } from "@/components/Landing";
import BookingPanel from "@/components/adlanding/BookingPanel";
import { StickyBar } from "@/components/adlanding/Parts";
import { OFFERS } from "@/data/landing";
import { getTeamMember, CLINIC } from "@/data/content";
import { HAIR_BOOKING_PAGE, HAIR_HERO } from "@/data/hair";
import { useLandingVisit } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";

const HairBooking = () => {
  const cfg = HAIR_BOOKING_PAGE;
  const truong = getTeamMember("truong");
  const { source, markInteracting } = useLandingVisit(cfg.path, cfg.serviceKey);

  useEffect(() => { trackEvent("hair_landing_view", { page: cfg.path, source }); }, [cfg.path, source]);

  const onInteract = useCallback(() => {
    trackEvent("hair_booking_started", { page: cfg.path, source, method: "widget" });
    markInteracting();
  }, [cfg.path, source, markInteracting]);
  const onCallback = useCallback(() => trackEvent("hair_callback_requested", { page: cfg.path, source }), [cfg.path, source]);

  return (
    <div data-testid="hair-booking-page">
      <SEO title={cfg.title} description={cfg.description} noindex />

      <section className="hair-dark relative bg-hair-dark lg:grid lg:grid-cols-5 lg:min-h-[92vh]" data-testid="hair-booking-hero">
        <div className="hidden lg:block absolute inset-y-0 left-0 w-3/5" aria-hidden="true">
          <img src={HAIR_HERO.room.id} alt="" loading="eager" decoding="async" style={{ objectPosition: HAIR_HERO.room.pos }} className="filmic absolute inset-0 w-full h-full object-cover" data-testid="hero-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(35,33,30,0.98)] via-[rgba(35,33,30,0.82)] to-[rgba(35,33,30,0.34)]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(35,33,30,0.94)] via-transparent to-[rgba(35,33,30,0.55)]" />
        </div>

        <div className="relative z-10 lg:col-span-3 px-6 sm:px-10 lg:pl-[120px] lg:pr-16 pt-[112px] pb-10 lg:py-28 flex flex-col justify-center">
          <div className="gold-frame inset-4 sm:inset-6 opacity-50 hidden lg:block" aria-hidden="true" />
          <Reveal y={16}>
            <p className="eyebrow eyebrow-dark">{cfg.eyebrow}</p>
            <h1 className="text-warm-white max-w-2xl" data-testid="hero-heading">{cfg.h1}</h1>
            <p className="standfirst text-warm-white/85 max-w-xl text-[19px] lg:text-[24px] !mt-5 lg:!mt-8" data-testid="hero-standfirst">{cfg.standfirst}</p>
          </Reveal>
          <Reveal y={12} delay={0.1}>
            <CompactPractitioner member={truong} className="mt-8" />
            <OfferLine text={OFFERS.hair} className="mt-6" />
            <ul className="flex flex-wrap gap-x-6 gap-y-2 mt-5" data-testid="hair-booking-offer-items">
              {cfg.offer.map((item) => (
                <li key={item} className="text-[11px] tracking-[0.2em] uppercase text-warm-white/70">{item}</li>
              ))}
            </ul>
            <ProofStrip variant="hair" className="mt-8" />
          </Reveal>
          <Reveal y={12} delay={0.15} className="mt-12">
            <NumberedColumns items={cfg.reasons} dark testId="hair-booking-reason" />
          </Reveal>
        </div>

        <div className="relative z-10 lg:col-span-2 bg-hair-panel px-5 sm:px-10 lg:px-10 py-8 lg:pt-28 lg:pb-16">
          <Reveal y={12} delay={0.15}>
            <BookingPanel
              page={cfg.path}
              source={source}
              heading={cfg.widgetHeading}
              widgetKey={cfg.widget}
              practitioner={truong.name}
              onInteract={onInteract}
              onCallback={onCallback}
            />
          </Reveal>
        </div>
      </section>

      <PathwayStrip />

      <HairBand dark testId="hair-booking-doctor">
        <Reveal><DoctorBlock dark heading="Assessed, planned and performed by Dr Kristy Truong" testId="hair-booking-doctor-block" /></Reveal>
      </HairBand>

      <PracticeStandards />

      <HairBand dark narrow testId="hair-booking-credentials">
        <Reveal>
          <HairEyebrow className="mb-4">Your doctor</HairEyebrow>
          <p className="text-warm-white/80 text-[15px] leading-relaxed" data-testid="hair-booking-credential-line">{cfg.credentials}</p>
          <HairHeading dark className="mt-12">Discretion</HairHeading>
          <p className="text-warm-white/80 leading-[1.8] mt-4">
            Lumière sits inside an established medical centre, so arriving for an assessment looks exactly like arriving for a GP appointment.
          </p>
          <p className="text-warm-white/50 text-sm mt-3">{CLINIC.address}</p>
        </Reveal>
      </HairBand>

      <div className="h-[72px] lg:hidden" aria-hidden="true" />
      <StickyBar targetId="booking-panel" />
    </div>
  );
};

export default HairBooking;
